import { BidAdjustmentJobFormData, BidPriceConstraintType } from 'core/bidAdjustmentJob/BidAdjustmentJob';
import { useCallAPI } from 'hooks/useCallAPI';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { validateEmpty, validateStringMaxLength } from 'utils/ValidateUtils';
import i18n from 'i18n';
import { BidAdjustmentJobManager, DefaultBidAdjustmentJobManager } from 'core/bidAdjustmentJob/BidAdjustmentJobManager';
import { isEmpty, omitBy } from 'lodash';
import { AdType } from 'core/rtbCampaign/RtbCampaign';
import { SelectOptions } from 'components/commonType';
import { AgencyManager, ChooseVendorStrategy, DefaultAgencyManager } from 'core';
import { useLocation } from 'react-router-dom';
import { adTypeOptimizeTypeMap } from './BidAdjustmentField';

export type BidAdjustmentJobFormModel = {
  loading: boolean;
  redirect?: string;
  initBidAdjustmentJob?: BidAdjustmentJobFormData;
  allVendorNumberOptions: SelectOptions[];
  validateForm: (values: BidAdjustmentJobFormData) => void;
  onSubmit: (values: BidAdjustmentJobFormData) => void;
};

const defaultBidAdjustmentJobManager: BidAdjustmentJobManager = new DefaultBidAdjustmentJobManager();
const defaultAgencyManager: AgencyManager = new DefaultAgencyManager();
export const useCreateBidAdjustmentJobFormModel = (): BidAdjustmentJobFormModel => {

  const { loading, callAPIs } = useCallAPI();
  const [redirect, setRedirect] = useState<string>();
  const [allVendorNumberOptions, setAllVendorNumberOptions] = useState<SelectOptions[]>([]);
  const { state } = useLocation<{ adType?: AdType } | undefined>();
  const initAdType = state?.adType ?? AdType.KEYWORD;

  const validateVendorNumbers = useCallback(
    (chooseVendorStrategy: ChooseVendorStrategy, vendorNumbers: string[]) => {
      if (chooseVendorStrategy === ChooseVendorStrategy.ALL) {
        return;
      }

      const error = validateEmpty(vendorNumbers);
      if (error) {
        return error;
      }
      const allVendorNumbers = allVendorNumberOptions.map(
        (option) => option.value
      );
      return vendorNumbers.some(
        (vendorNumber: string) => !allVendorNumbers.includes(vendorNumber)
      )
        ? i18n.t<string>('bidAdjustmentJobForm.errors.vendorNumbersInvalid')
        : undefined;
    },
    [allVendorNumberOptions]
  );

  const validateForm = useCallback((values: BidAdjustmentJobFormData) => {
    return omitBy(
      {
        purpose: validateStringMaxLength(
          values.purpose,
          50,
          i18n.t<string>('bidAdjustmentJobForm.errors.purposeLength')
        ),
        vendorNumbers: validateVendorNumbers(values.chooseVendorStrategy, values.vendorNumbers)
      },
      isEmpty
    );
  }, [validateVendorNumbers]);

  const onSubmit = useCallback((values: BidAdjustmentJobFormData) => {
    callAPIs([() => defaultBidAdjustmentJobManager.createBidAdjustmentJob(values)], () => {
      setRedirect('/bid-adjustments');
    });
  }, [callAPIs]);

  const initBidAdjustmentJob: BidAdjustmentJobFormData = useMemo(() => ({
    purpose: '',
    scheduledTime: moment().add(1, 'days').startOf('day').hour(4).format('YYYY-MM-DD HH:mm:ss'),
    vendorNumbers: [],
    isInstant: false,
    adjustments: [{
      bidPrice: 0,
      constraintType: BidPriceConstraintType.BID_FLOOR,
      adType: initAdType,
      optimizeType: adTypeOptimizeTypeMap[initAdType]
    }],
    chooseVendorStrategy: ChooseVendorStrategy.ALL
  }), [initAdType]);

  useEffect(() => {
    callAPIs([() => defaultAgencyManager.getAgencyVendorNumberOptions(true)], (vendorNumberOptions: SelectOptions[]) => {
      setAllVendorNumberOptions(vendorNumberOptions);
    });
  }, [callAPIs]);

  return {
    loading,
    redirect,
    initBidAdjustmentJob,
    allVendorNumberOptions,
    validateForm,
    onSubmit
  };
};
